import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  AlertCircle,
  Info,
  Clock,
  Mail,
  Target,
  FileText,
  Eye,
  CalendarIcon,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/auth';
import { useVerifiedDomains } from '@/hooks/useVerifiedDomains';
import EmailTriggerService, { EmailTriggerRule } from '@/api/emailTriggers';
import TriggerConditionBuilder from './TriggerConditionBuilder';
import EmailMessageComposition from './EmailMessageComposition';
import { DomainSelector } from '@/components/email/forms/DomainSelector';
import PreviewDialog from '@/components/email/preview/PreviewDialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Smartphone, Tablet, Monitor } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  TriggerFormStep,
  TriggerType,
  TriggerConditionFormData,
  ConditionField,
  ConditionOperator,
  TimeUnit,
  TRIGGER_TYPE_LABELS,
  TRIGGER_TYPE_DESCRIPTIONS,
  TIME_UNIT_LABELS,
} from '@/types/sms-triggers';

// TODO: Phase 7 - Create @/types/email-triggers and import from there
// For now, using SMS types as they are mostly compatible

interface EmailTriggerFormData {
  rule_name: string;
  rule_description: string;
  trigger_type: TriggerType;
  conditions: TriggerConditionFormData[];
  logic: 'AND' | 'OR';
  time_delay: { value: number; unit: TimeUnit };

  // Email-specific fields
  email_subject: string;
  email_content_html: string;
  email_content_text: string;
  email_design_json?: any;
  email_template_id?: string | null;  // Track which template was used
  email_domain_id: string;
  email_domain_name?: string; // Store actual domain name for constructing sender_email
  email_prefix: string;
  sender_name: string;

  personalization_fields?: string[];
  priority: number;
  max_sends_per_customer?: number;
  cooldown_days?: number;
  starts_at?: string;
  expires_at?: string;
  is_active: boolean;
}

interface EmailTriggerFormProps {
  triggerId?: string | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const FORM_STEPS: { id: TriggerFormStep; label: string; icon: React.ReactNode }[] = [
  { id: 'basic', label: 'Basic Info', icon: <FileText className="h-4 w-4" /> },
  { id: 'conditions', label: 'Conditions', icon: <Target className="h-4 w-4" /> },
  { id: 'message', label: 'Email Content', icon: <Mail className="h-4 w-4" /> },
  { id: 'timing', label: 'Timing', icon: <Clock className="h-4 w-4" /> },
  { id: 'review', label: 'Review', icon: <Eye className="h-4 w-4" /> }
];

const EmailTriggerForm: React.FC<EmailTriggerFormProps> = ({
  triggerId,
  onSuccess,
  onCancel
}) => {
  const { user } = useAuth();
  const { domains } = useVerifiedDomains(user?.organisationId || null);

  // TODO: Phase 7 - Create useEmailTriggers hook in @/hooks/email/useEmailTriggers
  // const { createTrigger, updateTrigger, loading: submitting } = useEmailTriggersManagement(user);
  const [submitting, setSubmitting] = useState(false);

  const [currentStep, setCurrentStep] = useState<TriggerFormStep>('basic');
  const [completedSteps, setCompletedSteps] = useState<Set<TriggerFormStep>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Progressive saving state management
  const [activeTriggerId, setActiveTriggerId] = useState<string | undefined>(triggerId || undefined);
  const [saving, setSaving] = useState(false);
  const [loadingTrigger, setLoadingTrigger] = useState(false);

  // Store references to GrapesJS save function and editor instance
  const editorSaveRef = useRef<(() => Promise<void>) | null>(null);
  const editorInstanceRef = useRef<any | null>(null);

  // Preview state
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [previewDeviceSize, setPreviewDeviceSize] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [resolvedPreview, setResolvedPreview] = useState<{
    html: string;
    subject: string;
    resolved_variables: Record<string, Record<string, string>>;
    customer: {
      id: string;
      email: string;
      first_name: string;
      last_name: string;
    };
  } | null>(null);

  // Customer selection state
  const [recentCustomers, setRecentCustomers] = useState<Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    total_orders?: number;
  }>>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

  const [formData, setFormData] = useState<EmailTriggerFormData>({
    rule_name: '',
    rule_description: '',
    trigger_type: 'milestone',
    conditions: [],
    logic: 'AND',
    time_delay: { value: 0, unit: 'minutes' },
    email_subject: '',
    email_content_html: '',
    email_content_text: '',
    email_design_json: undefined,
    email_template_id: null,
    email_domain_id: '',
    email_domain_name: '',
    email_prefix: '',
    sender_name: '',
    personalization_fields: [],
    priority: 100,
    max_sends_per_customer: 1,
    cooldown_days: undefined,
    is_active: false
  });

  // Date pickers state
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  // Handle trigger type changes - add/remove days_since_last_order condition as needed
  useEffect(() => {
    if (formData.trigger_type === 're_engagement') {
      // Find existing days_since_last_order condition
      const existingDaysCondition = formData.conditions.find(c => c.field === 'days_since_last_order');
      const otherConditions = formData.conditions.filter(c => c.field !== 'days_since_last_order');

      if (existingDaysCondition) {
        const updatedDaysCondition = {
          ...existingDaysCondition,
          isRequired: true
        };

        if (formData.conditions[0]?.field !== 'days_since_last_order' || !formData.conditions[0]?.isRequired) {
          setFormData(prev => ({
            ...prev,
            conditions: [updatedDaysCondition, ...otherConditions]
          }));
        }
      } else {
        const newConditions = [{
          id: `condition_${Date.now()}`,
          field: 'days_since_last_order' as ConditionField,
          operator: 'greater_than' as ConditionOperator,
          value: 30,
          field_type: 'number' as const,
          isRequired: true
        }, ...otherConditions];
        setFormData(prev => ({ ...prev, conditions: newConditions }));
      }

      if (formData.logic !== 'AND') {
        setFormData(prev => ({ ...prev, logic: 'AND' }));
      }
    } else {
      const hasRequiredDaysCondition = formData.conditions.some(c =>
        c.field === 'days_since_last_order' && c.isRequired === true
      );

      if (hasRequiredDaysCondition) {
        const filteredConditions = formData.conditions.filter(c =>
          !(c.field === 'days_since_last_order' && c.isRequired === true)
        );

        if (filteredConditions.length !== formData.conditions.length) {
          setFormData(prev => ({
            ...prev,
            conditions: filteredConditions
          }));
        }
      }
    }
  }, [formData.trigger_type]);

  // Sync start date with formData
  useEffect(() => {
    if (startDate) {
      updateFormData({ starts_at: startDate.toISOString() });
    } else {
      updateFormData({ starts_at: undefined });
    }
  }, [startDate]);

  // Sync end date with formData
  useEffect(() => {
    if (endDate) {
      updateFormData({ expires_at: endDate.toISOString() });
    } else {
      updateFormData({ expires_at: undefined });
    }
  }, [endDate]);

  // Fetch recent customers for preview
  useEffect(() => {
    const fetchRecentCustomers = async () => {
      if (!user?.organisationId) return;

      try {
        console.log('[Trigger Form] Fetching recent customers for preview');

        // Use EmailService to get recent customers (same as campaigns)
        const EmailService = (await import('@/api/email')).default;
        const customers = await EmailService.getRecentCustomers(
          user.organisationId,
          50  // Limit to 50 recent customers
        );

        setRecentCustomers(customers);

        // Auto-select first customer
        if (customers.length > 0) {
          setSelectedCustomerId(customers[0].id);
        }

        console.log('[Trigger Form] Loaded', customers.length, 'customers for preview');
      } catch (error) {
        console.error('[Trigger Form] Error fetching customers:', error);
        // Don't show error toast - this is not critical
      }
    };

    fetchRecentCustomers();
  }, [user?.organisationId]);

  /**
   * Load existing trigger data for editing
   * Fetches trigger from API when triggerId is provided
   */
  useEffect(() => {
    const fetchExistingTrigger = async () => {
      if (!triggerId || !user?.organisationId) return;

      console.log('[EmailTriggerForm] Loading existing trigger:', triggerId);
      setLoadingTrigger(true);

      try {
        const trigger = await EmailTriggerService.getTrigger(
          triggerId,
          user.organisationId
        );

        console.log('[EmailTriggerForm] Trigger loaded successfully');
        loadTriggerData(trigger);
      } catch (error: any) {
        console.error('[EmailTriggerForm] Error loading trigger:', error);
        toast({
          title: 'Error Loading Trigger',
          description: error.message || 'Failed to load trigger data',
          variant: 'destructive'
        });
      } finally {
        setLoadingTrigger(false);
      }
    };

    fetchExistingTrigger();
  }, [triggerId, user?.organisationId]);

  /**
   * Load template data from localStorage when creating new trigger
   * Templates are stored by EmailTriggerTemplates component
   */
  useEffect(() => {
    // Only check for template when creating (not editing)
    if (triggerId) return;

    const templateData = localStorage.getItem('selectedEmailTriggerTemplate');
    if (!templateData) return;

    try {
      const template = JSON.parse(templateData);
      console.log('[EmailTriggerForm] Loading template:', template.name);

      // Apply template data to form
      setFormData(prev => ({
        ...prev,
        rule_name: template.name || prev.rule_name,
        rule_description: template.description || prev.rule_description,
        trigger_type: template.trigger_type || prev.trigger_type,
        conditions: template.default_conditions || prev.conditions,
        logic: template.default_logic || prev.logic,
        time_delay: template.default_time_delay || prev.time_delay,
        priority: template.default_priority ?? prev.priority,
        max_sends_per_customer: template.default_max_sends_per_customer ?? prev.max_sends_per_customer,
        cooldown_days: template.default_cooldown_days ?? prev.cooldown_days,
        personalization_fields: template.personalization_fields || prev.personalization_fields
      }));

      // Clear localStorage after loading
      localStorage.removeItem('selectedEmailTriggerTemplate');

      toast({
        title: 'Template Loaded',
        description: `Template "${template.name}" has been applied to the form`,
      });
    } catch (error) {
      console.error('[EmailTriggerForm] Error loading template:', error);
      // Clear corrupted data
      localStorage.removeItem('selectedEmailTriggerTemplate');
    }
  }, [triggerId]);

  /**
   * Map database trigger type to form trigger type
   * Same as SMS (milestone vs re_engagement)
   */
  const mapDatabaseTypeToFormType = (dbType: string): TriggerType => {
    switch (dbType) {
      case 'customer_activity_update':
      case 'order_confirmed':
      case 'nth_order':
      case 'first_order':
      case 'spending_milestone':
        return 'milestone';
      case 'customer_inactive':
        return 're_engagement';
      default:
        return dbType as TriggerType;
    }
  };

  /**
   * Load trigger data from database into form state
   * Converts EmailTriggerRule to EmailTriggerFormData format
   */
  const loadTriggerData = (trigger: EmailTriggerRule) => {
    console.log('[EmailTriggerForm] Converting trigger data to form format');

    // 1. Map database trigger type to form trigger type first (needed for condition logic)
    const formTriggerType = mapDatabaseTypeToFormType(trigger.trigger_type);

    // 2. Convert database conditions object to array
    const conditions: TriggerConditionFormData[] = Object.entries(
      trigger.rule_conditions?.conditions || {}
    ).map(([field, condition]: [string, any], index) => ({
      id: `condition_${index}`,
      field: field as ConditionField,
      operator: condition.operator as ConditionOperator,
      value: condition.value,
      field_type: condition.field_type || 'string',
      // Mark days_since_last_order as required for re_engagement triggers
      // This prevents the useEffect from trying to reorganize conditions
      isRequired: formTriggerType === 're_engagement' && field === 'days_since_last_order' ? true : undefined
    }));

    // 3. Extract sender email components
    let emailPrefix = '';
    let emailDomainName = '';

    if (trigger.sender_email) {
      const [prefix, domain] = trigger.sender_email.split('@');
      emailPrefix = prefix || '';
      emailDomainName = domain || '';
    }

    // 4. Set form data
    setFormData({
      rule_name: trigger.rule_name,
      rule_description: trigger.rule_description || '',
      trigger_type: formTriggerType,
      conditions,
      logic: (trigger.rule_conditions?.logic || 'AND') as 'AND' | 'OR',
      time_delay: trigger.time_delay || { value: 0, unit: 'minutes' },

      // Email-specific fields
      email_subject: trigger.email_subject || '',
      email_content_html: trigger.email_content_html || '',
      email_content_text: trigger.email_content_text || '',
      email_design_json: trigger.email_design_json || undefined,
      email_template_id: trigger.email_template_id || null,
      email_domain_id: trigger.email_domain_id || '',
      email_domain_name: emailDomainName,
      email_prefix: emailPrefix,
      sender_name: trigger.sender_name || '',

      personalization_fields: trigger.personalization_fields || [],
      priority: trigger.priority || 100,
      max_sends_per_customer: trigger.max_sends_per_customer || 1,
      cooldown_days: trigger.cooldown_days,
      starts_at: trigger.starts_at,
      expires_at: trigger.expires_at,
      is_active: trigger.is_active || false
    });

    // 5. Set date states
    setStartDate(trigger.starts_at ? new Date(trigger.starts_at) : undefined);
    setEndDate(trigger.expires_at ? new Date(trigger.expires_at) : undefined);

    // 6. Email design will be loaded automatically by GrapesJSEditor via initialProject prop
    // No manual loading needed here - formData.email_design_json will flow to triggerData.email_design_json
    console.log('[EmailTriggerForm] Email design data loaded into form state');
  };

  const validateStep = (step: TriggerFormStep): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 'basic':
        if (!formData.rule_name.trim()) {
          newErrors.rule_name = 'Trigger name is required';
        }
        if (!formData.trigger_type) {
          newErrors.trigger_type = 'Trigger type is required';
        }
        // Email-specific validation
        if (!formData.email_domain_id) {
          newErrors.email_domain_id = 'Email domain is required';
        }
        if (!formData.email_prefix) {
          newErrors.email_prefix = 'Email prefix is required';
        }
        if (!formData.sender_name) {
          newErrors.sender_name = 'Sender name is required';
        }
        break;

      case 'conditions':
        if (formData.conditions.length === 0) {
          newErrors.conditions = 'At least one condition is required';
        }
        formData.conditions.forEach((condition, index) => {
          if (!condition.value && condition.operator !== 'in' && condition.operator !== 'not_in') {
            newErrors[`condition_${index}`] = 'Value is required';
          }
          if ((condition.operator === 'in' || condition.operator === 'not_in') &&
              (!Array.isArray(condition.value) || condition.value.length === 0)) {
            newErrors[`condition_${index}`] = 'At least one value must be selected';
          }
        });
        break;

      case 'message':
        if (!formData.email_subject.trim()) {
          newErrors.email_subject = 'Email subject is required';
        }
        if (!formData.email_content_html.trim()) {
          newErrors.email_content_html = 'Email content is required';
        }
        // TODO: Phase 4 - Add promo placeholder validation like SMS
        break;

      case 'timing':
        if (formData.time_delay.value < 0) {
          newErrors.time_delay = 'Time delay cannot be negative';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    // SPECIAL CASE: Message step (skip validation, content in GrapesJS)
    if (currentStep === 'message') {
      try {
        await saveCurrentStep();  // Triggers GrapesJS save
        console.log('[Trigger Form] Message step save completed successfully');
      } catch (error) {
        console.error('[Trigger Form] Message step save failed:', error);
        // Don't prevent navigation even if save fails - content might still be there
      }
    } else {
      // OTHER STEPS: Validate first, then save
      if (!validateStep(currentStep)) return;
      await saveCurrentStep(); // ✅ Save before moving to next step
    }

    // Navigate to next step
    setCompletedSteps(prev => new Set([...prev, currentStep]));

    const currentIndex = FORM_STEPS.findIndex(step => step.id === currentStep);
    if (currentIndex < FORM_STEPS.length - 1) {
      setCurrentStep(FORM_STEPS[currentIndex + 1].id);
      console.log('[Trigger Form] Step transition complete');
    }
  };

  const handlePrevious = () => {
    const currentIndex = FORM_STEPS.findIndex(step => step.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(FORM_STEPS[currentIndex - 1].id);
    }
  };

  const handleSubmit = async () => {
    // Validate all steps (except message - GrapesJS handles that)
    const stepsToValidate = FORM_STEPS.filter(step => step.id !== 'message');
    const allStepsValid = stepsToValidate.every(step => validateStep(step.id));

    if (!allStepsValid) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors before submitting',
        variant: 'destructive'
      });
      return;
    }

    try {
      setSubmitting(true);

      // Prepare submission data
      // IMPORTANT: Exclude email content fields (html, text, design_json) as they were
      // already saved by GrapesJS in Step 3. Using stale formData would overwrite them.
      const { email_content_html, email_content_text, email_design_json, ...formDataWithoutContent } = formData;

      let submissionData = {
        ...formDataWithoutContent,
        organisation_id: user?.organisationId,
        restaurant_id: user?.restaurantId || null,
        updated_by: user?.id
      };

      // For re_engagement triggers, convert hours to minutes if needed
      if (formData.trigger_type === 're_engagement' && formData.time_delay.unit === 'hours') {
        submissionData.time_delay = {
          value: formData.time_delay.value * 60,
          unit: 'minutes'
        };
      }

      // Use triggerId (edit mode) or activeTriggerId (progressive save)
      const currentTriggerId = triggerId || activeTriggerId;

      if (currentTriggerId) {
        // Update existing trigger
        await EmailTriggerService.updateTrigger(currentTriggerId, submissionData);
        console.log('[Trigger Form] Trigger updated:', currentTriggerId);
      } else {
        // Create new trigger (shouldn't happen with progressive saving, but handle it)
        const created = await EmailTriggerService.createTrigger({
          ...submissionData,
          created_by: user?.id
        });
        setActiveTriggerId(created.id);
        console.log('[Trigger Form] Trigger created:', created.id);
      }

      toast({
        title: currentTriggerId ? 'Trigger Updated' : 'Trigger Created',
        description: `Email trigger ${currentTriggerId ? 'updated' : 'created'} successfully`,
      });

      onSuccess();
    } catch (error) {
      console.error('Error saving trigger:', error);
      toast({
        title: 'Error',
        description: 'Failed to save trigger',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const updateFormData = (updates: Partial<EmailTriggerFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  /**
   * Save current step data to database
   * - Step 1 (basic): Create draft or update basic fields
   * - Step 2 (conditions): Update conditions
   * - Step 3 (message): Skip - GrapesJS handles it
   * - Step 4 (timing): Update timing fields
   */
  const saveCurrentStep = async () => {
    try {
      setSaving(true);

      // SPECIAL HANDLING FOR MESSAGE STEP
      // Trigger GrapesJS save first to ensure content is up-to-date
      if (currentStep === 'message' && editorSaveRef.current) {
        console.log('[Trigger Form] Triggering GrapesJS save before step transition');
        try {
          await editorSaveRef.current();  // Calls editor.store()
          // Wait 300ms for React state updates to complete
          await new Promise(resolve => setTimeout(resolve, 300));
          console.log('[Trigger Form] Waited for state update to complete');
        } catch (error) {
          // GrapesJS may throw errors even after successful save (e.g., autosave conflicts)
          // Log but don't fail - the content was likely saved successfully
          console.warn('[Trigger Form] GrapesJS save had an error (content may still be saved):', error);
        }
      }

      // Don't save during loading
      if (loadingTrigger) {
        console.log('[Trigger Form] Skipping save - loading trigger data');
        return;
      }

      // CREATE DRAFT if doesn't exist (Step 1 only)
      // Skip if editing existing trigger (triggerId provided)
      if (!activeTriggerId && !triggerId && currentStep === 'basic' && user?.organisationId) {
        console.log('[Trigger Form] Creating draft trigger...');

        // Prepare draft data with PLACEHOLDER content
        // Note: Backend validation expects 'conditions' as array, not 'rule_conditions' as object
        // ⚠️ Backend requires at least one condition, so add placeholder that matches all customers
        const draftData = {
          rule_name: formData.rule_name,
          rule_description: formData.rule_description,
          trigger_type: formData.trigger_type,
          conditions: [
            {
              field: 'total_orders',
              operator: 'greater_than_or_equal',
              value: 0  // Matches all customers (placeholder condition)
            }
          ],
          logic: 'AND',
          time_delay: { value: 0, unit: 'minutes' }, // Default timing
          email_subject: formData.email_subject || 'Draft Email Subject',
          email_content_html: '<p>Start designing your trigger email...</p>', // ✅ Placeholder HTML
          email_content_text: 'Start designing your trigger email...', // ✅ Placeholder text
          email_design_json: undefined, // Will be populated in Step 3
          email_template_id: null, // Will be set when user loads a template
          email_domain_id: formData.email_domain_id,
          sender_email: formData.email_prefix && formData.email_domain_name
            ? `${formData.email_prefix}@${formData.email_domain_name}` // ✅ Construct proper email
            : undefined,
          sender_name: formData.sender_name,
          priority: formData.priority,
          max_sends_per_customer: formData.max_sends_per_customer,
          cooldown_days: formData.cooldown_days,
          is_active: false,  // ✅ Draft starts inactive
          organisation_id: user.organisationId,
          restaurant_id: user.restaurantId || null,
          created_by: user.id
        };

        const draft = await EmailTriggerService.createTrigger(draftData);
        setActiveTriggerId(draft.id);  // ✅ Store trigger ID for future updates
        console.log('[Trigger Form] Draft trigger created:', draft.id);
        return; // Exit early after creation
      }

      // UPDATE EXISTING TRIGGER based on current step
      // Use triggerId (edit mode) or activeTriggerId (progressive save)
      const currentTriggerId = triggerId || activeTriggerId;

      if (!currentTriggerId) {
        console.warn('[Trigger Form] No trigger ID available for autosave');
        return;
      }

      let updateData: any = {};

      // STEP 1: Basic Info
      if (currentStep === 'basic') {
        updateData = {
          rule_name: formData.rule_name,
          rule_description: formData.rule_description,
          trigger_type: formData.trigger_type,
          email_domain_id: formData.email_domain_id,
          sender_email: formData.email_prefix && formData.email_domain_name
            ? `${formData.email_prefix}@${formData.email_domain_name}` // ✅ Construct proper email
            : undefined,
          sender_name: formData.sender_name,
          priority: formData.priority,
          max_sends_per_customer: formData.max_sends_per_customer,
          cooldown_days: formData.cooldown_days,
          organisation_id: user?.organisationId,
          updated_by: user?.id
        };
      }

      // STEP 2: Conditions
      else if (currentStep === 'conditions') {
        updateData = {
          conditions: formData.conditions,  // ✅ Send as array - backend converts to rule_conditions
          logic: formData.logic,
          organisation_id: user?.organisationId,
          updated_by: user?.id
        };
      }

      // STEP 3: Message
      else if (currentStep === 'message') {
        // ⚠️ IMPORTANT: Email content is already saved by GrapesJS storage manager
        // But we still need to save the email_subject and email_template_id which may have been updated
        updateData = {
          email_subject: formData.email_subject,
          email_template_id: formData.email_template_id || null,  // ✅ Save template ID for tracking
          organisation_id: user?.organisationId,
          updated_by: user?.id
        };
        console.log('[Trigger Form] Message step - saving email_subject and email_template_id (content saved by GrapesJS)');
      }

      // STEP 4: Timing
      else if (currentStep === 'timing') {
        updateData = {
          time_delay: formData.time_delay,
          starts_at: formData.starts_at,
          expires_at: formData.expires_at,
          organisation_id: user?.organisationId,
          updated_by: user?.id
        };
      }

      // Only save if there's data to update
      if (Object.keys(updateData).length > 0) {
        await EmailTriggerService.updateTrigger(currentTriggerId, updateData);
        console.log(`[Trigger Form] Auto-saved ${currentStep} step data for trigger:`, currentTriggerId);
      }
    } catch (error) {
      console.error(`[Trigger Form] Error auto-saving ${currentStep} step:`, error);
      // Don't show error toast for autosave failures - just log it
      // The user can continue and try again on final save
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle clicking on a step indicator in the progress bar
   * Saves current step before navigating to prevent data loss
   * Special handling for message step (GrapesJS save)
   */
  const handleStepClick = async (targetStep: TriggerFormStep) => {
    // Don't navigate if clicking on current step
    if (targetStep === currentStep) {
      console.log('[Trigger Form] Already on step:', targetStep);
      return;
    }

    console.log(`[Trigger Form] Step navigation: ${currentStep} → ${targetStep}`);

    // Determine navigation direction
    const currentIndex = FORM_STEPS.findIndex(s => s.id === currentStep);
    const targetIndex = FORM_STEPS.findIndex(s => s.id === targetStep);
    const isMovingForward = targetIndex > currentIndex;

    // SPECIAL CASE: Message step (skip validation, content in GrapesJS)
    if (currentStep === 'message') {
      try {
        await saveCurrentStep();  // Triggers GrapesJS save
        console.log('[Trigger Form] Message step save completed before navigation');
      } catch (error) {
        console.error('[Trigger Form] Message step save failed:', error);
        // Don't prevent navigation - content might still be saved
        // Only show error for forward navigation
        if (isMovingForward) {
          toast({
            title: 'Save Warning',
            description: 'Content save encountered an error, but you can continue. Try saving manually if needed.',
            variant: 'default'
          });
        }
      }
    } else {
      // OTHER STEPS: Validate when moving forward, skip when moving backward
      if (isMovingForward) {
        // Validate before proceeding forward
        if (!validateStep(currentStep)) {
          toast({
            title: 'Validation Error',
            description: 'Please fix the errors before proceeding',
            variant: 'destructive'
          });
          return; // Block forward navigation
        }
      }
      // Always save current step data (even when moving backward)
      try {
        await saveCurrentStep();
        console.log(`[Trigger Form] ${currentStep} step saved before navigation`);
      } catch (error) {
        console.error(`[Trigger Form] ${currentStep} step save failed:`, error);
        // Only block forward navigation on save errors
        if (isMovingForward) {
          toast({
            title: 'Save Error',
            description: 'Failed to save current step. Please try again.',
            variant: 'destructive'
          });
          return;
        }
        // Allow backward navigation even with save errors (user might be fixing issues)
      }
    }

    // Mark current step as completed if moving forward
    if (isMovingForward) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
    }

    // Navigate to target step
    setCurrentStep(targetStep);
    console.log(`[Trigger Form] Navigation complete: now on ${targetStep}`);
  };

  const getStepStatus = (stepId: TriggerFormStep) => {
    if (completedSteps.has(stepId)) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'upcoming';
  };

  const isStepClickable = (stepId: TriggerFormStep) => {
    if (completedSteps.has(stepId)) return true;
    if (stepId === currentStep) return true;

    const stepIndex = FORM_STEPS.findIndex(step => step.id === stepId);
    if (stepIndex > 0) {
      const previousStep = FORM_STEPS[stepIndex - 1];
      if (completedSteps.has(previousStep.id)) {
        const allPreviousCompleted = FORM_STEPS.slice(0, stepIndex).every(
          step => completedSteps.has(step.id)
        );
        return allPreviousCompleted;
      }
    }

    return false;
  };

  /**
   * Handle preview button click - Show customer selector
   */
  const handlePreviewEmail = () => {
    console.log('[Trigger Form] Preview requested');

    // Validate we have email content
    if (!formData.email_content_html || formData.email_content_html === '<p>Start designing...</p>') {
      toast({
        title: 'No Content',
        description: 'Please design your email content first (Step 3)',
        variant: 'destructive'
      });
      return;
    }

    // Check if we have customers
    if (recentCustomers.length === 0) {
      toast({
        title: 'No Customers Available',
        description: 'Add customers to your organization before previewing with real data.',
        variant: 'destructive'
      });
      return;
    }

    // Show customer selector dialog
    setShowCustomerSelector(true);
  };

  /**
   * Generate preview with selected customer data
   * Calls backend to resolve all variables with real customer data
   */
  const handleGeneratePreview = async () => {
    if (!selectedCustomerId || !user?.organisationId) {
      toast({
        title: 'Error',
        description: 'Please select a customer',
        variant: 'destructive'
      });
      return;
    }

    // For NEW triggers (not saved yet), we need activeTriggerId
    // If activeTriggerId doesn't exist, we can't preview with backend
    // because backend needs trigger_id to fetch content
    if (!activeTriggerId) {
      toast({
        title: 'Save Required',
        description: 'Please save your trigger as a draft before previewing (click Next to auto-save)',
        variant: 'destructive'
      });
      return;
    }

    setPreviewLoading(true);
    try {
      console.log('[Trigger Form] Generating preview with customer:', selectedCustomerId);

      // Call backend to resolve variables with real customer data
      const preview = await EmailTriggerService.previewTriggerWithCustomer(
        activeTriggerId,
        selectedCustomerId,
        user.organisationId
      );

      // Close customer selector
      setShowCustomerSelector(false);

      // Open preview dialog
      setResolvedPreview(preview);
      setShowPreviewDialog(true);

      toast({
        title: 'Success',
        description: 'Preview generated with actual customer data',
        variant: 'default'
      });

      console.log('[Trigger Form] Preview generated successfully');
    } catch (error: any) {
      console.error('[Trigger Form] Error generating preview:', error);
      toast({
        title: 'Error Generating Preview',
        description: error.message || 'Failed to generate preview',
        variant: 'destructive'
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  /**
   * Extract all {{variables}} from email content and subject
   * Used to show variable list in review step
   */
  const extractVariables = (text: string): string[] => {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const matches = text.matchAll(variableRegex);
    const variables = new Set<string>();

    for (const match of matches) {
      variables.add(`{{${match[1]}}}`);
    }

    return Array.from(variables).sort();
  };

  // Show loading state while fetching trigger
  if (loadingTrigger) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading trigger data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full mx-auto space-y-6">
      {/* Progress Steps */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between gap-2 overflow-x-auto scrollbar-hide">
            {FORM_STEPS.map((step, index) => {
              const status = getStepStatus(step.id);
              const isClickable = isStepClickable(step.id);
              return (
                <div key={step.id} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => isClickable && handleStepClick(step.id)}
                    disabled={!isClickable}
                    className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 transition-colors flex-shrink-0 ${
                      status === 'completed'
                        ? 'bg-green-600 border-green-600 text-white hover:bg-green-700'
                        : status === 'current'
                        ? 'border-primary text-primary'
                        : 'border-muted-foreground text-muted-foreground'
                    } ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                  >
                    {status === 'completed' ? (
                      <Check className="h-4 w-4 sm:h-5 sm:w-5 stroke-white" />
                    ) : (
                      <div className="h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center">
                        {step.icon}
                      </div>
                    )}
                  </button>
                  <div className="ml-3 hidden sm:block">
                    <p className={`text-sm font-medium ${
                      status === 'current' ? 'text-primary' : status === 'completed' ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {step.label}
                    </p>
                  </div>
                  {index < FORM_STEPS.length - 1 && (
                    <div className={`w-8 sm:w-12 h-0.5 mx-2 sm:mx-4 flex-shrink-0 ${
                      completedSteps.has(FORM_STEPS[index + 1].id)
                        ? 'bg-green-600'
                        : 'bg-border'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Saving Indicator */}
      {saving && (
        <Alert>
          <Loader2 className="h-4 w-4 animate-spin" />
          <AlertDescription>
            Saving your progress...
          </AlertDescription>
        </Alert>
      )}

      {/* Form Content */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          {/* Step 1: Basic Info - ADAPTED for Email */}
          {currentStep === 'basic' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Basic Information</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Set up the basic details for your email trigger
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="rule_name">Trigger Name *</Label>
                  <Input
                    id="rule_name"
                    value={formData.rule_name}
                    onChange={(e) => updateFormData({ rule_name: e.target.value })}
                    placeholder="e.g., Welcome New Customers"
                  />
                  {errors.rule_name && (
                    <p className="text-sm text-destructive">{errors.rule_name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="trigger_type">Trigger Type *</Label>
                  <Select
                    value={formData.trigger_type}
                    onValueChange={(value) => updateFormData({ trigger_type: value as TriggerType })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select trigger type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TRIGGER_TYPE_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.trigger_type && (
                    <p className="text-sm text-destructive">{errors.trigger_type}</p>
                  )}
                  {formData.trigger_type && TRIGGER_TYPE_DESCRIPTIONS[formData.trigger_type] && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {TRIGGER_TYPE_DESCRIPTIONS[formData.trigger_type]}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rule_description">Description</Label>
                <Textarea
                  id="rule_description"
                  value={formData.rule_description}
                  onChange={(e) => updateFormData({ rule_description: e.target.value })}
                  placeholder="Describe what this trigger does and when it should be used"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Input
                    id="priority"
                    type="number"
                    value={formData.priority}
                    onChange={(e) => updateFormData({ priority: parseInt(e.target.value) || 100 })}
                    placeholder="100"
                    min="1"
                    max="1000"
                  />
                  <p className="text-xs text-muted-foreground">
                    Lower numbers = higher priority
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_sends">Max Sends per Customer</Label>
                  <Input
                    id="max_sends"
                    type="number"
                    value={formData.max_sends_per_customer || ''}
                    onChange={(e) => updateFormData({
                      max_sends_per_customer: e.target.value ? parseInt(e.target.value) : undefined
                    })}
                    placeholder="No limit"
                    min="1"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave empty for no limit
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cooldown">Cooldown (days)</Label>
                  <Input
                    id="cooldown"
                    type="number"
                    value={formData.cooldown_days || ''}
                    onChange={(e) => updateFormData({
                      cooldown_days: e.target.value ? parseInt(e.target.value) : undefined
                    })}
                    placeholder="No cooldown"
                    min="1"
                  />
                  <p className="text-xs text-muted-foreground">
                    Wait period between triggers
                  </p>
                </div>
              </div>

              {/* EMAIL-SPECIFIC: Domain Selector */}
              <div className="space-y-2">
                <Label>Email Domain & Sender *</Label>
                <DomainSelector
                  organisationId={user?.organisationId || null}
                  selectedDomainId={formData.email_domain_id}
                  emailPrefix={formData.email_prefix}
                  senderName={formData.sender_name}
                  onDomainChange={(domainId) => {
                    // Also store the domain name for constructing sender_email
                    const selectedDomain = domains.find(d => d.id === domainId);
                    updateFormData({
                      email_domain_id: domainId,
                      email_domain_name: selectedDomain?.domain || ''
                    });
                  }}
                  onEmailPrefixChange={(prefix) => updateFormData({ email_prefix: prefix })}
                  onSenderNameChange={(name) => updateFormData({ sender_name: name })}
                />
                {(errors.email_domain_id || errors.email_prefix || errors.sender_name) && (
                  <p className="text-sm text-destructive">
                    {errors.email_domain_id || errors.email_prefix || errors.sender_name}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Conditions - 100% REUSABLE (AS-IS) */}
          {currentStep === 'conditions' && (
            <TriggerConditionBuilder
              conditions={formData.conditions}
              logic={formData.logic}
              onChange={(conditions, logic) => updateFormData({ conditions, logic })}
              errors={errors}
              triggerType={formData.trigger_type}
            />
          )}

          {/* Step 3: Email Content - Full HTML Editor (Phase 4) */}
          {currentStep === 'message' && (
            <EmailMessageComposition
              triggerData={{
                email_subject: formData.email_subject,
                email_content_html: formData.email_content_html,
                email_content_text: formData.email_content_text,
                email_design_json: formData.email_design_json,
                trigger_id: activeTriggerId  // ✅ Use activeTriggerId for GrapesJS persistence
              }}
              updateTriggerData={(data) => {
                updateFormData(data);
              }}
              errors={errors}
              onEditorReady={(saveFunction, editorInstance) => {
                console.log('[Trigger Form] GrapesJS editor ready, capturing refs');
                editorSaveRef.current = saveFunction;
                editorInstanceRef.current = editorInstance;
              }}
            />
          )}

          {/* Step 4: Timing - 100% REUSABLE (AS-IS, cosmetic text change only) */}
          {currentStep === 'timing' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Timing & Scheduling</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  {formData.trigger_type === 're_engagement'
                    ? 'Configure when the email should be sent before the customer\'s typical order time'
                    : 'Configure when the email should be sent after the trigger conditions are met'
                  }
                </p>
              </div>

              <div className="space-y-6">
                <div className="space-y-4">
                  <Label>
                    {formData.trigger_type === 're_engagement'
                      ? 'Send Time (before typical order time)'
                      : 'Time Delay'
                    }
                  </Label>
                  <div className="flex items-center space-x-4">
                    <Input
                      type="number"
                      value={formData.time_delay.value}
                      onChange={(e) => updateFormData({
                        time_delay: { ...formData.time_delay, value: parseInt(e.target.value) || 0 }
                      })}
                      className="w-24"
                      min="0"
                    />
                    <Select
                      value={formData.time_delay.unit}
                      onValueChange={(value) => updateFormData({
                        time_delay: { ...formData.time_delay, unit: value as TimeUnit }
                      })}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {formData.trigger_type === 're_engagement'
                          ? (
                            <>
                              <SelectItem value="minutes">Minutes</SelectItem>
                              <SelectItem value="hours">Hours</SelectItem>
                            </>
                          ) : (
                            Object.entries(TIME_UNIT_LABELS).map(([value, label]) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            ))
                          )
                        }
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {formData.trigger_type === 're_engagement'
                      ? (formData.time_delay.value === 0
                        ? 'Email will be sent at the customer\'s typical order time'
                        : `Email will be sent ${formData.time_delay.value} ${formData.time_delay.unit} before typical order time`
                      )
                      : (formData.time_delay.value === 0
                        ? 'Email will be sent immediately after conditions are met'
                        : `Email will be sent ${formData.time_delay.value} ${formData.time_delay.unit} after conditions are met`
                      )
                    }
                  </p>
                  {errors.time_delay && (
                    <p className="text-sm text-destructive">{errors.time_delay}</p>
                  )}
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="starts_at">Start Date (Optional)</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {startDate ? format(startDate, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={setStartDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-muted-foreground">
                      Trigger will not activate before this date
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="expires_at">End Date (Optional)</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate ? format(endDate, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={setEndDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-muted-foreground">
                      Trigger will stop activating after this date
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Review - BASIC for Phase 2, will enhance in Phase 6 */}
          {currentStep === 'review' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Review & Confirm</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Review your trigger configuration before saving
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Basic Info</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-sm font-medium">Name:</dt>
                        <dd className="text-sm text-muted-foreground">{formData.rule_name}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium">Type:</dt>
                        <dd className="text-sm text-muted-foreground">
                          {TRIGGER_TYPE_LABELS[formData.trigger_type]}
                        </dd>
                      </div>
                      {formData.rule_description && (
                        <div>
                          <dt className="text-sm font-medium">Description:</dt>
                          <dd className="text-sm text-muted-foreground">{formData.rule_description}</dd>
                        </div>
                      )}
                    </dl>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Email Settings</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-sm font-medium">From:</dt>
                        <dd className="text-sm text-muted-foreground font-mono">
                          {formData.email_prefix}@{formData.email_domain_name || ''}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium">Sender Name:</dt>
                        <dd className="text-sm text-muted-foreground">{formData.sender_name}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium">Subject:</dt>
                        <dd className="text-sm text-muted-foreground">{formData.email_subject}</dd>
                      </div>
                    </dl>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base">Conditions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground mb-2">
                        Trigger when {formData.logic === 'AND' ? 'ALL' : 'ANY'} of:
                      </p>
                      {formData.conditions.map((condition, index) => (
                        <div key={condition.id} className="flex items-center space-x-2">
                          {index > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {formData.logic}
                            </Badge>
                          )}
                          <span className="text-sm">
                            {condition.field} {condition.operator} {' '}
                            {Array.isArray(condition.value)
                              ? condition.value.join(', ')
                              : typeof condition.value === 'object' && condition.value !== null && 'min' in condition.value && 'max' in condition.value
                              ? `${condition.value.min || 'min'} to ${condition.value.max || 'max'}`
                              : condition.value
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base">Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Initial Status</p>
                        <p className="text-sm text-muted-foreground">
                          {formData.is_active ? 'Active - Trigger will start processing immediately' : 'Paused - Trigger will be saved but not active'}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Label htmlFor="is_active" className="text-sm">
                          {formData.is_active ? 'Active' : 'Paused'}
                        </Label>
                        <Switch
                          id="is_active"
                          checked={formData.is_active ?? false}
                          onCheckedChange={(checked) => updateFormData({ is_active: checked })}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Email Preview Card - ENHANCED WITH REAL DATA */}
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base">Email Preview</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Review how your email will look when sent to customers
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Subject Line */}
                    <div>
                      <Label className="text-xs text-muted-foreground">Subject Line:</Label>
                      <p className="text-sm font-medium mt-1">{formData.email_subject}</p>
                    </div>

                    <Separator />

                    {/* Device Size Toggle */}
                    <div className="space-y-3">
                      <Label className="text-xs text-muted-foreground">Preview Size:</Label>
                      <div className="flex gap-2">
                        <Button
                          variant={previewDeviceSize === 'mobile' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPreviewDeviceSize('mobile')}
                          type="button"
                        >
                          <Smartphone className="h-4 w-4 mr-2" />
                          Mobile
                        </Button>
                        <Button
                          variant={previewDeviceSize === 'tablet' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPreviewDeviceSize('tablet')}
                          type="button"
                        >
                          <Tablet className="h-4 w-4 mr-2" />
                          Tablet
                        </Button>
                        <Button
                          variant={previewDeviceSize === 'desktop' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPreviewDeviceSize('desktop')}
                          type="button"
                        >
                          <Monitor className="h-4 w-4 mr-2" />
                          Desktop
                        </Button>
                      </div>
                    </div>

                    {/* Email Preview Iframe */}
                    <div className={cn(
                      'border rounded overflow-hidden bg-white transition-all duration-300',
                      previewDeviceSize === 'mobile' && 'max-w-[375px] mx-auto',
                      previewDeviceSize === 'tablet' && 'max-w-[768px] mx-auto',
                      previewDeviceSize === 'desktop' && 'w-full'
                    )}>
                      <iframe
                        srcDoc={formData.email_content_html}
                        className="w-full border-0"
                        style={{ height: '400px' }}
                        title="Email Preview"
                        sandbox="allow-same-origin"
                      />
                    </div>

                    {/* Variable List (if any) */}
                    {(() => {
                      const extractedVariables = extractVariables(
                        formData.email_content_html + formData.email_subject
                      );
                      return extractedVariables.length > 0 ? (
                        <div className="mt-4">
                          <Label className="text-xs text-muted-foreground">
                            Variables in this email ({extractedVariables.length}):
                          </Label>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {extractedVariables.map(variable => (
                              <Badge key={variable} variant="secondary" className="font-mono text-xs">
                                {variable}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            Preview with customer data to see actual values
                          </p>
                        </div>
                      ) : null;
                    })()}

                    {/* Preview with Real Customer Data Button */}
                    <Button
                      onClick={handlePreviewEmail}
                      variant="outline"
                      className="w-full"
                      type="button"
                      disabled={!activeTriggerId}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Preview with Customer Data
                    </Button>

                    {!activeTriggerId && (
                      <p className="text-xs text-muted-foreground text-center">
                        Save as draft first by clicking "Next" to enable preview
                      </p>
                    )}

                    <p className="text-xs text-muted-foreground text-center">
                      + One-click unsubscribe link will be automatically included
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={currentStep === 'basic' ? onCancel : handlePrevious}
        >
          {currentStep === 'basic' ? 'Cancel' : (
            <>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Previous
            </>
          )}
        </Button>

        <div className="flex space-x-2">
          {currentStep === 'review' ? (
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Saving...' : (triggerId ? 'Update Trigger' : 'Create Trigger')}
            </Button>
          ) : (
            <Button onClick={handleNext}>
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>

      {/* Customer Selector Dialog */}
      <Dialog open={showCustomerSelector} onOpenChange={setShowCustomerSelector}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Preview with Customer Data</DialogTitle>
            <DialogDescription>
              Select a customer to preview this trigger with their actual data
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Trigger Info */}
            <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
              <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{formData.rule_name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {formData.email_subject}
                </p>
              </div>
            </div>

            {/* Customer Selector */}
            <div className="space-y-2">
              <Label htmlFor="customer-select">Select Customer</Label>
              <Select
                value={selectedCustomerId || ''}
                onValueChange={setSelectedCustomerId}
              >
                <SelectTrigger id="customer-select">
                  <SelectValue placeholder="Choose a customer" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {recentCustomers.map(customer => (
                    <SelectItem key={customer.id} value={customer.id}>
                      <div className="flex items-center justify-between w-full">
                        <span>
                          {customer.first_name} {customer.last_name}
                        </span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {customer.email}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Variables like {'{{firstName}}'} will be replaced with this customer's actual data
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCustomerSelector(false)}
              disabled={previewLoading}
              type="button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGeneratePreview}
              disabled={!selectedCustomerId || previewLoading}
              type="button"
            >
              {previewLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Generate Preview
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog with Real Customer Data */}
      {resolvedPreview && (
        <PreviewDialog
          open={showPreviewDialog}
          onOpenChange={setShowPreviewDialog}
          resolvedPreview={resolvedPreview}
        />
      )}
    </div>
  );
};

export default EmailTriggerForm;